import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_BASE_URL from '../config';
import { ArrowLeft, Save } from 'lucide-react';

const GymForm = () => {
  const navigate = useNavigate();
  const [gymName, setGymName] = useState('');
  const [ownerFullName, setOwnerFullName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [city, setCity] = useState('');
  const [planTier, setPlanTier] = useState('free');
  const [status, setStatus] = useState('trial');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [hardwareSerial, setHardwareSerial] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gymName || !ownerFullName || !ownerEmail || !ownerPhone || !city) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');

      const payload = {
        gymName,
        ownerFullName,
        ownerEmail,
        ownerPhone,
        city,
        planTier,
        status,
        trialEndsAt: trialEndsAt ? new Date(trialEndsAt).toISOString() : null,
        hardwareSerial: hardwareSerial || null,
        internalNotes: internalNotes || null
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/gyms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to onboard the new gym.');
      }

      if (data.success) {
        navigate('/admin/gyms');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while communicating with the server. Simulated onboarding.');
      // Local redirection for simulated flow
      setTimeout(() => {
        navigate('/admin/gyms');
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
      {/* Back link */}
      <div>
        <Link to="/admin/gyms" className="inline-flex items-center text-xs font-semibold text-gray-400 hover:text-white uppercase tracking-wider">
          <ArrowLeft size={14} className="mr-1" /> Back to Gym Roster
        </Link>
        <h1 className="text-3xl font-bold text-white tracking-tight mt-2">Onboard New Gym</h1>
        <p className="text-gray-400 text-sm mt-1">Register workspace profile and seed owner account credentials.</p>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4">
          {error}
        </div>
      )}

      {/* Form Container */}
      <form onSubmit={handleSubmit} className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg p-6 lg:p-8 space-y-8">
        
        {/* Section 1: Gym Details */}
        <div>
          <h3 className="text-sm font-bold text-[#6CD800] uppercase tracking-wider border-b border-[#1E293B] pb-2 mb-6">1. Gym Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Gym Name *</label>
              <input
                type="text"
                value={gymName}
                onChange={(e) => setGymName(e.target.value)}
                placeholder="e.g. Muscle Factory"
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">City / Location *</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Hyderabad"
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
                required
              />
            </div>
          </div>
        </div>

        {/* Section 2: Owner Credentials */}
        <div>
          <h3 className="text-sm font-bold text-[#6CD800] uppercase tracking-wider border-b border-[#1E293B] pb-2 mb-6">2. Gym Owner Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Owner Full Name *</label>
              <input
                type="text"
                value={ownerFullName}
                onChange={(e) => setOwnerFullName(e.target.value)}
                placeholder="e.g. Rajesh Kumar"
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
                required
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Owner Email *</label>
              <input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="owner@gmail.com"
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
                required
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Owner Phone * (For Login)</label>
              <input
                type="tel"
                value={ownerPhone}
                onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="e.g. 9876543210"
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
                required
              />
            </div>
          </div>
        </div>

        {/* Section 3: Setup & Subscription */}
        <div>
          <h3 className="text-sm font-bold text-[#6CD800] uppercase tracking-wider border-b border-[#1E293B] pb-2 mb-6">3. Setup & Subscriptions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Plan Tier</label>
              <select
                value={planTier}
                onChange={(e) => setPlanTier(e.target.value)}
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm focus:outline-none focus:border-[#6CD800]"
              >
                <option value="free">Free</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm focus:outline-none focus:border-[#6CD800]"
              >
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Trial Ends Date</label>
              <input
                type="date"
                value={trialEndsAt}
                onChange={(e) => setTrialEndsAt(e.target.value)}
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm focus:outline-none focus:border-[#6CD800]"
              />
            </div>
          </div>
        </div>

        {/* Section 4: Hardware & Admin Notes */}
        <div>
          <h3 className="text-sm font-bold text-[#6CD800] uppercase tracking-wider border-b border-[#1E293B] pb-2 mb-6">4. Hardware & Administrative Notes</h3>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Hardware Device Serial No.</label>
              <input
                type="text"
                value={hardwareSerial}
                onChange={(e) => setHardwareSerial(e.target.value)}
                placeholder="e.g. FS-001 (Raspberry Pi Serial ID)"
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Internal Notes (Admin-only)</label>
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Enter details on specific pilot agreements, pending items, or custom deals..."
                rows={4}
                className="w-full px-4 py-2.5 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#6CD800]"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 border-t border-[#1E293B] pt-6">
          <Link
            to="/admin/gyms"
            className="px-6 py-2.5 bg-[#1E293B] border border-[#334155] hover:border-gray-500 rounded-lg text-sm text-gray-300 font-semibold transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-6 py-2.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-sm transition-colors"
          >
            <Save size={16} className="mr-2" />
            {loading ? 'Onboarding...' : 'Save Workspace'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default GymForm;
